import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, AlertCircle, Plus, Edit, Trash2, Filter, X,
  FileText, Truck, User, Phone, MapPin, Calendar, CheckSquare,
  ChevronDown, ChevronRight, ClipboardList, Package,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/date-formatter';
import useAuth from '../../hooks/useAuth';
import SearchableSelect from './SearchableSelect';
import { nowLocalInput } from '../../utils/time';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ENTRY_POINTS = ['daryal', 'murphy', 'no_need'];
const EXIT_POINTS  = ['bagratashen', 'gogavan', 'no_need'];
const STATUSES     = ['on_road', 'completed', 'cancelled'];

const EMPTY_FORM = {
  created_by: '', company: '', number: '', trailer_number: '',
  departure_date: nowLocalInput(),
  comment: '', status: 'on_road', entry_point: '', exit_point: '',
  terminal_entry_date: '', terminal_exit_date: '', parking_40: false,
  exitPoint_entry_date: '', exitPoint_exit_date: '',
  natakhtari_entry_date: '', natakhtari_exit_date: '',
  transit_in: 0, transit_out: 0, ticket: '',
  driver_name: '', phone: '',
};

const EMPTY_BULK = {
  user_id: '', trucks: [],
  departure_date: nowLocalInput(),
  status: 'on_road', entry_point: '', exit_point: '', transit_in: 0,
};

function authHeaders(json = true) {
  const token = localStorage.getItem('auth_token');
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

function getStatusBadge(s) {
  if (s === 'on_road')   return 'bg-yellow-100 text-yellow-800';
  if (s === 'completed') return 'bg-green-100 text-green-800';
  if (s === 'cancelled') return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-800';
}

function toDT(val) { return val ? val.slice(0, 16) : ''; }

const inp = 'block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed';
const inpPl = 'block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed';
const Label = ({ children, req }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1">
    {children}{req && ' *'}
  </label>
);
const IconWrap = ({ icon: Icon }) => (
  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
    <Icon className="h-5 w-5 text-gray-400" />
  </div>
);

export default function AdminRegistrations() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [rows, setRows]       = useState([]);
  const [users, setUsers]     = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [truckQuery, setTruckQuery] = useState('');
  const [selUser, setSelUser]       = useState('');
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [stats, setStats]     = useState({ total: 0, transit_in: 0, transit_out: 0, parking_40: 0, parking_entry: 0, parking_exit: 0 });
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [pageSize, setPageSize]   = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const [bulkModal, setBulkModal] = useState(false);
  const [bulk, setBulk]       = useState(EMPTY_BULK);
  const [bulkTrucks, setBulkTrucks] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkOverrides, setBulkOverrides] = useState({});
  const [expandedOverrides, setExpandedOverrides] = useState({});

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    try {
      const r = await fetch(`${BASE_URL}/auth/users/`, { headers: authHeaders(false) });
      if (r.ok) setUsers(await r.json());
    } catch {}
  }, []);

  const fetchData = useCallback(async (uid, q, st) => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams();
      if (uid) p.set('created_by', uid);
      if (q)   p.set('number', q);
      if (st)  p.set('status', st);
      const r = await fetch(`${BASE_URL}/registrations/?${p}`, { headers: authHeaders(false) });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setRows(Array.isArray(data) ? data : (data.results || []));
      if (data.stats) setStats(data.stats);
    } catch { setError('Failed to load registrations.'); }
    finally { setLoading(false); }
  }, []);

  // All vehicles (every user) — the create/edit form picks a vehicle first and
  // derives its owner, so we load them all and filter client-side.
  const loadAllVehicles = useCallback(async () => {
    setVehiclesLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/vehicles/admin-vehicles/`, { headers: authHeaders(false) });
      setAllVehicles(r.ok ? await r.json() : []);
    } catch { setAllVehicles([]); }
    finally { setVehiclesLoading(false); }
  }, []);

  const fetchBulkTrucks = useCallback(async (userId) => {
    if (!userId) { setBulkTrucks([]); return; }
    try {
      const r = await fetch(`${BASE_URL}/vehicles/admin-vehicles/?user_id=${userId}&type=truck`, { headers: authHeaders(false) });
      setBulkTrucks(r.ok ? await r.json() : []);
    } catch { setBulkTrucks([]); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchData(selUser, search, filterStatus); setCurrentPage(1); }, [selUser, fetchData]);

  // ── Modals ────────────────────────────────────────────────────────────────

  const openCreate = () => {
    loadAllVehicles();
    setForm(EMPTY_FORM);
    setModal({ mode: 'create' });
  };

  const openEdit = (r) => {
    loadAllVehicles();
    setForm({
      ...EMPTY_FORM, ...r,
      created_by:           r.created_by ?? '',
      departure_date:       toDT(r.departure_date),
      terminal_entry_date:  toDT(r.terminal_entry_date),
      terminal_exit_date:   toDT(r.terminal_exit_date),
      exitPoint_entry_date: toDT(r.exitPoint_entry_date),
      exitPoint_exit_date:  toDT(r.exitPoint_exit_date),
      natakhtari_entry_date:toDT(r.natakhtari_entry_date),
      natakhtari_exit_date: toDT(r.natakhtari_exit_date),
      transit_in:  r.transit_in  ?? 0,
      transit_out: r.transit_out ?? 0,
      ticket:  r.ticket  || '',
      comment: r.comment || '',
    });
    setModal({ mode: 'edit', id: r.id });
  };

  // ── Form handlers ─────────────────────────────────────────────────────────

  const f = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [k]: v }));
  };

  const usernameById = (id) => users.find(u => String(u.id) === String(id))?.username || '';
  const isTruck   = (v) => ['truck', 'tractor'].includes(v.type);
  const isTrailer = (v) => v.type === 'semi_trailer';

  // Pick a user first (optional): narrows the vehicle list, clears the vehicle.
  const selectOwner = (uid) => {
    setForm(prev => ({
      ...prev,
      created_by: uid,
      company: usernameById(uid) || (uid ? prev.company : ''),
      number: '', trailer_number: '',
    }));
  };

  // Pick a vehicle → auto-fill its owner (created_by + company) and plate.
  const selectVehicle = (vid) => {
    const veh = allVehicles.find(v => String(v.id) === String(vid));
    if (!veh) return;
    setForm(prev => ({
      ...prev,
      number: veh.plate_number || '',
      created_by: veh.created_by_id ?? prev.created_by,
      company: usernameById(veh.created_by_id) || prev.company,
      trailer_number: '',
    }));
  };

  const selectTrailer = (vid) => {
    const veh = allVehicles.find(v => String(v.id) === String(vid));
    setForm(prev => ({ ...prev, trailer_number: veh?.plate_number || '' }));
  };

  // ── Save / Delete ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    try {
      const isEdit = modal.mode === 'edit';
      const payload = { ...form };
      // Insurance is no longer edited here — never send it.
      delete payload.truck_insurance;
      delete payload.trailer_insurance;
      ['terminal_entry_date','terminal_exit_date','exitPoint_entry_date',
       'exitPoint_exit_date','natakhtari_entry_date','natakhtari_exit_date']
        .forEach(k => { if (!payload[k]) payload[k] = null; });
      const res = await fetch(
        isEdit ? `${BASE_URL}/registrations/${modal.id}/` : `${BASE_URL}/registrations/`,
        { method: isEdit ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(payload) }
      );
      if (!res.ok) throw new Error(await res.text());
      setModal(null);
      fetchData(selUser, search, filterStatus);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${BASE_URL}/registrations/${id}/`, { method: 'DELETE', headers: authHeaders(false) });
      setDeleteId(null);
      fetchData(selUser, search, filterStatus);
    } catch { alert('Failed to delete.'); }
  };

  // ── Bulk create ───────────────────────────────────────────────────────────

  const handleBulkUserChange = (e) => {
    const uid = e.target.value;
    setBulk(prev => ({ ...prev, user_id: uid, trucks: [] }));
    setBulkOverrides({});
    setExpandedOverrides({});
    fetchBulkTrucks(uid);
  };

  const setOverride = (plate, field, value) => {
    setBulkOverrides(prev => ({ ...prev, [plate]: { ...prev[plate], [field]: value } }));
  };

  const toggleBulkTruck = (plate) => {
    setBulk(prev => ({
      ...prev,
      trucks: prev.trucks.includes(plate) ? prev.trucks.filter(t => t !== plate) : [...prev.trucks, plate],
    }));
  };

  const handleBulkCreate = async () => {
    setBulkSaving(true);
    try {
      const payload = { ...bulk, overrides: bulkOverrides };
      const res = await fetch(`${BASE_URL}/registrations/bulk-create/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Bulk create failed');
      alert(`Created: ${data.created}, Skipped: ${data.skipped}`);
      setBulkModal(false);
      setBulk(EMPTY_BULK);
      setBulkTrucks([]);
      setBulkOverrides({});
      setExpandedOverrides({});
      fetchData(selUser, search, filterStatus);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setBulkSaving(false); }
  };


  // ── Sync from trcp.ge ────────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await fetch(`${BASE_URL}/registrations/sync-from-site/`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await r.json();
      if (r.ok) {
        setSyncMsg(`Updated: ${data.updated}`);
        fetchData(selUser, search, filterStatus);
      } else {
        setSyncMsg(data.error || 'Sync failed');
      }
    } catch { setSyncMsg('Sync failed'); }
    finally { setSyncing(false); }
  };

  // ── Section header helper ─────────────────────────────────────────────────
  const SH = ({ text }) => (
    <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200">{text}</h3>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  // Everything but the User + Truck pickers is locked until a vehicle is chosen.
  const locked = !form.number;
  const truckOptions = allVehicles
    .filter(v => isTruck(v) && (!form.created_by || String(v.created_by_id) === String(form.created_by)))
    .map(v => ({
      value: v.id,
      label: form.created_by
        ? v.plate_number
        : `${v.plate_number}${usernameById(v.created_by_id) ? ' — ' + usernameById(v.created_by_id) : ''}`,
    }));
  const trailerOptions = allVehicles
    .filter(v => isTrailer(v) && String(v.created_by_id) === String(form.created_by))
    .map(v => ({ value: v.id, label: v.plate_number }));
  const currentTruckId = allVehicles.find(
    v => v.plate_number === form.number && isTruck(v)
  )?.id || '';
  // Show the "not in fleet" note when the fleet is empty OR the current search
  // matches no vehicle.
  const truckSearchNoMatch = !!truckQuery &&
    !truckOptions.some(o => String(o.label).toLowerCase().includes(truckQuery.toLowerCase()));
  const showNoTruckNote = !vehiclesLoading && (truckOptions.length === 0 || truckSearchNoMatch);
  const currentTrailerId = allVehicles.find(
    v => v.plate_number === form.trailer_number && isTrailer(v)
  )?.id || '';

  return (
    <div className="container mx-auto space-y-4">

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{t('registrations')}</h1>
          <select value={selUser} onChange={e => setSelUser(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white">
            <option value="">{t('all')} users</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-1 sm:gap-2">
          <button onClick={openCreate}
            className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center text-sm">
            <Plus className="h-4 w-4 mr-1" /> {t('register')}
          </button>
          <button onClick={() => { setBulk(EMPTY_BULK); setBulkTrucks([]); setBulkOverrides({}); setExpandedOverrides({}); setBulkModal(true); }}
            className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center text-sm">
            <CheckSquare className="h-4 w-4 mr-1" /> {t('bulkCreate')}
          </button>
          <button onClick={() => setFilterOpen(o => !o)}
            className={`px-3 py-2 rounded-md flex items-center text-sm ${filterOpen ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            <Filter className="h-4 w-4 mr-1" /> {t('filters')}
          </button>
          <button onClick={() => fetchData(selUser, search, filterStatus)} disabled={loading}
            className="px-3 py-2 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> {t('refresh')}
          </button>
          {isSuperAdmin && (
            <button onClick={handleSync} disabled={syncing}
              className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-sm disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> {t('syncFromSite')}
            </button>
          )}
        </div>
      </div>
      {syncMsg && (
        <div className={`text-sm px-3 py-1.5 rounded-md ${syncMsg.startsWith('Updated') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {syncMsg}
        </div>
      )}


      {/* Filters */}
      {filterOpen && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <form onSubmit={e => { e.preventDefault(); fetchData(selUser, search, filterStatus); }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('truckNumber')}</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={t('truckNumber')}
                  className="pl-8 block w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('status')}</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={inp}>
                <option value="">{t('all')}</option>
                {STATUSES.map(s => <option key={s} value={s}>{t(s)}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button type="submit"
                className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center justify-center text-sm">
                <Search className="h-4 w-4 mr-1" /> {t('search')}
              </button>
              <button type="button"
                onClick={() => { setSearch(''); setFilterStatus(''); setSelUser(''); fetchData('','',''); }}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 text-sm">
                {t('reset')}
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Remaining stats — shown only when a user is selected */}
      {stats.remaining && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
          {[
            { key: 'transit_card_remain', label: t('totalTransitRemain') },
            { key: 'parking_exit_remain', label: t('parkDaryalRemain') + ', ' + t('parkBagrGogremain') },
          ].map(({ key, label }) => {
            const val = stats.remaining[key] ?? 0;
            const textCls  = val <= 0 ? 'text-red-600'    : val <= 5 ? 'text-yellow-600'    : 'text-green-600';
            const bgCls    = val <= 0 ? 'bg-red-100'      : val <= 5 ? 'bg-yellow-100'      : 'bg-green-100';
            return (
              <div key={key} className="bg-white p-3 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className={`text-2xl font-bold ${textCls}`}>{val}</p>
                  </div>
                  <div className={`p-2 rounded-full ${bgCls}`}>
                    <Package className={`h-5 w-5 ${textCls}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      {(() => {
        const totalPages = Math.ceil(rows.length / pageSize);
        const paged = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
        return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <h3 className="text-sm font-medium text-gray-800">{t('registrationList')}</h3>
        </div>
        {/* Mobile card view */}
        <div className="block lg:hidden overflow-y-auto max-h-[calc(100vh-500px)]">
          {loading ? (
            <div className="p-8 text-center flex justify-center items-center">
              <RefreshCw className="animate-spin h-5 w-5 text-primary-600 mr-2" />
              <span className="text-sm text-gray-500">{t('loading')}</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-sm">{t('noRegistrationsFound')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {paged.map(r => (
                <div key={r.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-primary-600 text-sm">{r.number}</p>
                      <p className="text-xs text-gray-500">{r.company || '—'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(r.status)}`}>
                        {t(r.status)}
                      </span>
                      {r.status === 'completed' && !isSuperAdmin ? (
                        <>
                          <button disabled className="text-gray-300 p-1 rounded-full cursor-not-allowed">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button disabled className="text-gray-300 p-1 rounded-full cursor-not-allowed">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => openEdit(r)}
                            className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleteId(r.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div>
                      <span className="text-gray-500">{t('departureDate')}: </span>
                      <span className="text-gray-700">{formatDate(r.departure_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('transitInOut')}: </span>
                      <span className="inline-flex gap-1">
                        <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-800">{t('IN')}: {r.transit_in || 0}</span>
                        <span className="px-1 py-0.5 rounded bg-red-100 text-red-800">{t('OUT')}: {r.transit_out || 0}</span>
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('entryPoint')}: </span>
                      <span className="text-gray-700">{t(r.entry_point || '-')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('exitPoint')}: </span>
                      <span className="text-gray-700">{t(r.exit_point || '-')}</span>
                    </div>
                    {r.ticket && (
                      <div>
                        <span className="text-gray-500">{t('ticket')}: </span>
                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{r.ticket}</span>
                      </div>
                    )}
                    {r.parking_40 && (
                      <div>
                        <span className="text-gray-500">{t('parking_40')}: </span>
                        <span className="text-green-500 font-bold">✓</span>
                      </div>
                    )}
                    {r.terminal_entry_date && (
                      <div className="col-span-2">
                        <span className="text-gray-500">{t('terminalEntryDate')}: </span>
                        <span className="text-gray-700">{formatDate(r.terminal_entry_date)}</span>
                      </div>
                    )}
                    {r.terminal_exit_date && (
                      <div className="col-span-2">
                        <span className="text-gray-500">{t('terminalExitDate')}: </span>
                        <span className="text-gray-700">{formatDate(r.terminal_exit_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop table view */}
        <div className="hidden lg:block overflow-auto max-h-[calc(100vh-500px)]">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('truckNumber')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('trailerNumber')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('company')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('status')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('transitInOut')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('departureDate')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('entryPoint')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('terminalEntryDate')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('terminalExitDate')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('ticket')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('parking_40')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('exitPoint')}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={13} className="px-4 py-8 text-center">
                  <div className="flex justify-center items-center">
                    <RefreshCw className="animate-spin h-5 w-5 text-primary-600 mr-2" />
                    <span className="text-sm text-gray-500">{t('loading')}</span>
                  </div>
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-gray-500">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm">{t('noRegistrationsFound')}</p>
                </td></tr>
              ) : paged.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-primary-600 font-medium text-center">{r.number}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">{r.trailer_number || '—'}</td>
                  <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate text-center">{r.company || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(r.status)}`}>
                      {t(r.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">
                    <div className="flex gap-1 justify-center">
                      <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                        {t('IN')}: {r.transit_in || 0}
                      </span>
                      <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-800">
                        {t('OUT')}: {r.transit_out || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center text-xs">
                    {formatDate(r.departure_date)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                    {t(r.entry_point || '-')}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center text-xs">
                    {formatDate(r.terminal_entry_date)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center text-xs">
                    {formatDate(r.terminal_exit_date)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">
                    {r.ticket
                      ? <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{r.ticket}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">
                    {r.parking_40
                      ? <span className="text-green-500 font-bold">✓</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                    {t(r.exit_point || '-')}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-1">
                      {r.status === 'completed' && !isSuperAdmin ? (
                        <>
                          <button disabled className="text-gray-300 p-1 rounded-full cursor-not-allowed">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button disabled className="text-gray-300 p-1 rounded-full cursor-not-allowed">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => openEdit(r)}
                            className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleteId(r.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination — directly below table rows */}
        {!loading && rows.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, rows.length)} / {rows.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}

        {/* Footer stats */}
        {!loading && rows.length > 0 && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 px-4 py-3 rounded-b-lg">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-blue-100 rounded-full">
                    <ClipboardList className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-800">
                    {t('Total')}: <span className="text-blue-600">{stats.total}</span>
                  </span>
                </div>
                <span className="text-xs text-gray-400 uppercase tracking-wide">{t('registrationList')}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div className="flex flex-col items-center p-2 bg-white rounded-md shadow-sm border border-gray-200">
                  <span className="text-xs text-gray-500 text-center mb-1">{t('transitIn_All')}</span>
                  <span className="text-lg font-bold text-blue-600">{stats.transit_in || 0}</span>
                </div>
                <div className="flex flex-col items-center p-2 bg-white rounded-md shadow-sm border border-gray-200">
                  <span className="text-xs text-gray-500 text-center mb-1">{t('transitOut_All')}</span>
                  <span className="text-lg font-bold text-red-600">{stats.transit_out || 0}</span>
                </div>
                <div className="flex flex-col items-center p-2 bg-white rounded-md shadow-sm border border-gray-200">
                  <span className="text-xs text-gray-500 text-center mb-1">{t('parking_40_All')}</span>
                  <span className="text-lg font-bold text-purple-600">{stats.parking_40 || 0}</span>
                </div>
                <div className="flex flex-col items-center p-2 bg-white rounded-md shadow-sm border border-gray-200">
                  <span className="text-xs text-gray-500 text-center mb-1">{t('parking_entry_All')}</span>
                  <span className="text-lg font-bold text-orange-600">{stats.parking_entry || 0}</span>
                </div>
                <div className="flex flex-col items-center p-2 bg-white rounded-md shadow-sm border border-gray-200">
                  <span className="text-xs text-gray-500 text-center mb-1">{t('parking_exit_All')}</span>
                  <span className="text-lg font-bold text-orange-600">{stats.parking_exit || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        );
      })()}

      {/* ── Create / Edit modal ───────────────────────────────────────────── */}
      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden flex flex-col my-4">

            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white flex-shrink-0">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Truck className="h-5 w-5" />
                {modal.mode === 'create' ? t('register') : t('edit')}
              </h2>
              <button onClick={() => setModal(null)} className="p-2 hover:text-gray-200 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 space-y-6 max-h-[75vh]">

              {/* 1. Basic Information — pick user (optional) then vehicle */}
              <SH text={t('basicInfo')} />
              {locked && (
                <p className="-mt-2 text-xs text-amber-600">{t('selectVehicleFirst')}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 -mt-2">
                <div>
                  <Label>User</Label>
                  <SearchableSelect
                    icon={<User className="h-5 w-5 text-gray-400" />}
                    placeholder={`— ${t('select')} —`}
                    value={form.created_by}
                    options={[{ value: '', label: `${t('all')} users` }, ...users.map(u => ({ value: u.id, label: u.username }))]}
                    onChange={selectOwner}
                  />
                </div>
                <div>
                  <Label req>{t('truckNumber')}</Label>
                  {showNoTruckNote && (
                    <p className="mb-1 text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {truckOptions.length === 0 ? t('noVehiclesFound') : t('vehicleNotInFleet')}
                    </p>
                  )}
                  <SearchableSelect
                    icon={<Truck className="h-5 w-5 text-gray-400" />}
                    placeholder={`— ${t('select')} —`}
                    value={currentTruckId}
                    options={truckOptions}
                    onChange={selectVehicle}
                    emptyLabel={truckQuery ? t('vehicleNotInFleet') : t('noVehiclesFound')}
                    onQueryChange={setTruckQuery}
                  />
                </div>
              </div>

              {/* Everything below is disabled until a vehicle is selected */}
              <fieldset disabled={locked} className="space-y-6 disabled:opacity-60">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label req>{t('departureDate')}</Label>
                  <div className="relative">
                    <IconWrap icon={Calendar} />
                    <input type="datetime-local" value={form.departure_date} onChange={f('departure_date')} className={inpPl} />
                  </div>
                </div>
                <div>
                  <Label>{t('trailerNumber')}</Label>
                  <SearchableSelect
                    icon={<Truck className="h-5 w-5 text-gray-400" />}
                    placeholder={`— ${t('select')} —`}
                    value={currentTrailerId}
                    options={trailerOptions}
                    onChange={selectTrailer}
                    emptyLabel={t('noVehiclesFound')}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>{t('comment')}</Label>
                  <textarea value={form.comment} onChange={f('comment')} rows={2} className={inp} />
                </div>
              </div>

              {/* 2. Status & Points */}
              <SH text={t('statusPoints')} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 -mt-2">
                <div>
                  <Label>{t('status')}</Label>
                  <select value={form.status} onChange={f('status')} disabled className={inp}>
                    {STATUSES.map(s => <option key={s} value={s}>{t(s)}</option>)}
                  </select>
                </div>
                <div>
                  <Label req>{t('entryPoint')}</Label>
                  <div className="relative">
                    <IconWrap icon={MapPin} />
                    <select value={form.entry_point} onChange={f('entry_point')} className={inpPl}>
                      <option value="">{t('select')}</option>
                      {ENTRY_POINTS.map(s => <option key={s} value={s}>{t(s)}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label req>{t('exitPoint')}</Label>
                  <div className="relative">
                    <IconWrap icon={MapPin} />
                    <select value={form.exit_point} onChange={f('exit_point')} className={inpPl}>
                      <option value="">{t('select')}</option>
                      {EXIT_POINTS.map(s => <option key={s} value={s}>{t(s)}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>{t('terminalEntryDate')}</Label>
                  <input type="datetime-local" value={form.terminal_entry_date} onChange={f('terminal_entry_date')} disabled className={inp} />
                </div>
                <div>
                  <Label>{t('terminalExitDate')}</Label>
                  <input type="datetime-local" value={form.terminal_exit_date} onChange={f('terminal_exit_date')} disabled className={inp} />
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="adm_p40" checked={form.parking_40} onChange={f('parking_40')} disabled
                    className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50" />
                  <label htmlFor="adm_p40" className="text-sm font-medium text-gray-700">{t('parking_40')}</label>
                </div>
              </div>

              {/* 3. Parkings Entry & Exit */}
              <SH text={t('parkingsEntryExit')} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 -mt-2">
                <div>
                  <Label>{t('exitPointEntryDate')}</Label>
                  <input type="datetime-local" value={form.exitPoint_entry_date} onChange={f('exitPoint_entry_date')} disabled className={inp} />
                </div>
                <div>
                  <Label>{t('exitPointExitDate')}</Label>
                  <input type="datetime-local" value={form.exitPoint_exit_date} onChange={f('exitPoint_exit_date')} disabled className={inp} />
                </div>
                <div>
                  <Label>{t('natakhtariEntryDate')}</Label>
                  <input type="datetime-local" value={form.natakhtari_entry_date} onChange={f('natakhtari_entry_date')} disabled className={inp} />
                </div>
                <div>
                  <Label>{t('natakhtariExitDate')}</Label>
                  <input type="datetime-local" value={form.natakhtari_exit_date} onChange={f('natakhtari_exit_date')} disabled className={inp} />
                </div>
              </div>

              {/* 4. Transit Information */}
              <SH text={t('transitInfo')} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 -mt-2">
                <div>
                  <Label>{t('transitIn')}</Label>
                  <input type="number" min="0" value={form.transit_in} onChange={f('transit_in')} className={inp} />
                </div>
                <div>
                  <Label>{t('transitOut')}</Label>
                  <input type="number" min="0" value={form.transit_out} onChange={f('transit_out')} disabled className={inp} />
                </div>
                <div>
                  <Label>{t('ticket')}</Label>
                  <input value={form.ticket} onChange={f('ticket')} disabled className={inp} />
                </div>
              </div>

              {/* 5. Driver Information */}
              <SH text={t('driverInfo')} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 -mt-2">
                <div>
                  <Label>{t('name')}</Label>
                  <div className="relative">
                    <IconWrap icon={User} />
                    <input value={form.driver_name} onChange={f('driver_name')} className={inpPl} />
                  </div>
                </div>
                <div>
                  <Label>{t('phone')}</Label>
                  <div className="relative">
                    <IconWrap icon={Phone} />
                    <input value={form.phone} onChange={f('phone')} className={inpPl} />
                  </div>
                </div>
              </div>

              </fieldset>

            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
              <button onClick={() => setModal(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
                {t('cancel')}
              </button>
              <button onClick={handleSave} disabled={saving || locked}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50">
                {saving ? 'Saving…' : (modal.mode === 'create' ? t('save') : t('update'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Create modal ─────────────────────────────────────────────── */}
      {bulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden flex flex-col my-4">

            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white flex-shrink-0">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <CheckSquare className="h-5 w-5" /> {t('bulkCreate')}
              </h2>
              <button onClick={() => setBulkModal(false)} className="p-2 hover:text-gray-200 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 space-y-4 max-h-[70vh]">
              <div>
                <Label req>User</Label>
                <select value={bulk.user_id} onChange={handleBulkUserChange} className={inp}>
                  <option value="">{t('select')}</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
                </select>
              </div>

              {bulk.user_id && (
                <div>
                  <Label req>{t('truckNumber')} ({t('select')})</Label>
                  {bulkTrucks.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">{t('noVehiclesFound')}</p>
                  ) : (
                    <div className="border border-gray-300 rounded-md p-3 max-h-72 overflow-y-auto space-y-1">
                      {bulkTrucks.map(v => {
                        const isChecked = bulk.trucks.includes(v.plate_number);
                        const isExpanded = !!expandedOverrides[v.plate_number];
                        const ov = bulkOverrides[v.plate_number] || {};
                        return (
                          <div key={v.plate_number}>
                            <div className="flex items-center gap-2 hover:bg-gray-50 px-2 py-1 rounded">
                              <input type="checkbox" checked={isChecked}
                                onChange={() => toggleBulkTruck(v.plate_number)}
                                className="h-4 w-4 text-primary-600 border-gray-300 rounded cursor-pointer" />
                              <span className="text-sm font-medium text-gray-700 flex-1 cursor-pointer"
                                onClick={() => toggleBulkTruck(v.plate_number)}>{v.plate_number}</span>
                              {isChecked && (
                                <button type="button"
                                  onClick={() => setExpandedOverrides(prev => ({ ...prev, [v.plate_number]: !prev[v.plate_number] }))}
                                  className="flex items-center gap-0.5 text-xs text-primary-600 hover:text-primary-800 px-1.5 py-0.5 rounded border border-primary-200 hover:bg-primary-50">
                                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  Override
                                </button>
                              )}
                            </div>
                            {isChecked && isExpanded && (
                              <div className="ml-6 mt-1 mb-2 p-3 bg-gray-50 rounded-md border border-gray-200 grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('departureDate')}</label>
                                  <input type="datetime-local" value={ov.departure_date || ''}
                                    onChange={e => setOverride(v.plate_number, 'departure_date', e.target.value)}
                                    className="block w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('transitIn')}</label>
                                  <input type="number" min="0" value={ov.transit_in ?? ''}
                                    placeholder={String(bulk.transit_in)}
                                    onChange={e => setOverride(v.plate_number, 'transit_in', e.target.value)}
                                    className="block w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('entryPoint')}</label>
                                  <select value={ov.entry_point || ''}
                                    onChange={e => setOverride(v.plate_number, 'entry_point', e.target.value)}
                                    className="block w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500">
                                    <option value="">— default —</option>
                                    {ENTRY_POINTS.map(s => <option key={s} value={s}>{t(s)}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('exitPoint')}</label>
                                  <select value={ov.exit_point || ''}
                                    onChange={e => setOverride(v.plate_number, 'exit_point', e.target.value)}
                                    className="block w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500">
                                    <option value="">— default —</option>
                                    {EXIT_POINTS.map(s => <option key={s} value={s}>{t(s)}</option>)}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label req>{t('departureDate')}</Label>
                  <input type="datetime-local" value={bulk.departure_date}
                    onChange={e => setBulk(p => ({ ...p, departure_date: e.target.value }))} className={inp} />
                </div>
                <div>
                  <Label>{t('status')}</Label>
                  <select value={bulk.status}
                    onChange={e => setBulk(p => ({ ...p, status: e.target.value }))} className={inp}>
                    {STATUSES.map(s => <option key={s} value={s}>{t(s)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>{t('entryPoint')}</Label>
                  <select value={bulk.entry_point}
                    onChange={e => setBulk(p => ({ ...p, entry_point: e.target.value }))} className={inp}>
                    <option value="">{t('select')}</option>
                    {ENTRY_POINTS.map(s => <option key={s} value={s}>{t(s)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>{t('exitPoint')}</Label>
                  <select value={bulk.exit_point}
                    onChange={e => setBulk(p => ({ ...p, exit_point: e.target.value }))} className={inp}>
                    <option value="">{t('select')}</option>
                    {EXIT_POINTS.map(s => <option key={s} value={s}>{t(s)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>{t('transitIn')}</Label>
                  <input type="number" min="0" value={bulk.transit_in}
                    onChange={e => setBulk(p => ({ ...p, transit_in: e.target.value }))} className={inp} />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
              <button onClick={() => setBulkModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
                {t('cancel')}
              </button>
              <button onClick={handleBulkCreate} disabled={bulkSaving || bulk.trucks.length === 0}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50">
                {bulkSaving ? 'Creating…' : `${t('save')} (${bulk.trucks.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden w-80">
            <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
              <h3 className="font-semibold">{t('delete')}</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">{t('confirmDelete')}</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50">
                  {t('cancel')}
                </button>
                <button onClick={() => handleDelete(deleteId)}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700">
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
